import React, { useEffect, useMemo } from "react";
import * as THREE from "three";
import { observer } from "mobx-react-lite";
import { FloorplannerStoreContext } from "../../../../store/floorplannerStore";
import { WallType } from "../../../../types/floorplannerTypes";
import { DEFAULT_FLOOR_THICKNESS } from "../floors/floorGeometry";
import {
  Ceiling3DShapeDefinition,
  WallEndpointHeights,
  createSlopedCeilingEdgeGeometry,
  createSlopedCeilingGeometry,
} from "../floors/floorGeometry";
import CatalogStandardMaterial from "../../materials/CatalogStandardMaterial";
import { disablePointerRaycast } from "../../interaction/disablePointerRaycast";

const HIDDEN_EDGE_COLOR = 0x6f675e;
const HIDDEN_EDGE_OPACITY = 0.38;
/** Fallback when lighting.ceilingColor is missing (matches prior wall-default look). */
export const DEFAULT_CEILING_COLOR = 0xd8d0c4;

export type Ceiling3DLayerProps = {
  ceilings: Ceiling3DShapeDefinition[];
  walls: WallType[];
  wallEndpointHeightMap: Map<string, WallEndpointHeights>;
  hiddenCeilingIds?: Set<string>;
};

const Ceiling3DLayer: React.FC<Ceiling3DLayerProps> = observer(
  ({ ceilings, walls, wallEndpointHeightMap, hiddenCeilingIds }) => {
    const floorplannerStore = React.useContext(FloorplannerStoreContext);
    const color =
      floorplannerStore.lighting.ceilingColor ?? DEFAULT_CEILING_COLOR;

    return (
      <>
        {ceilings.map((ceiling) => {
          return (
            <SlopedCeilingSurface
              key={`ceiling-${ceiling.id}`}
              ceiling={ceiling}
              walls={walls}
              wallEndpointHeightMap={wallEndpointHeightMap}
              hidden={hiddenCeilingIds?.has(ceiling.id) ?? false}
              color={color}
              materialDefaults={floorplannerStore.threeDMaterialDefaults.wall}
            />
          );
        })}
      </>
    );
  },
);

const SlopedCeilingSurface: React.FC<{
  ceiling: Ceiling3DShapeDefinition;
  walls: WallType[];
  wallEndpointHeightMap: Map<string, WallEndpointHeights>;
  hidden: boolean;
  color: THREE.ColorRepresentation;
  materialDefaults: React.ComponentProps<
    typeof CatalogStandardMaterial
  >["defaults"];
}> = ({
  ceiling,
  walls,
  wallEndpointHeightMap,
  hidden,
  color,
  materialDefaults,
}) => {
  const geometry = useMemo(
    () =>
      hidden
        ? createSlopedCeilingEdgeGeometry(
            ceiling.shape,
            walls,
            wallEndpointHeightMap,
            ceiling.roomObjectIds,
            ceiling.height,
          )
        : createSlopedCeilingGeometry(
            ceiling.shape,
            walls,
            wallEndpointHeightMap,
            {
              wallIds: ceiling.roomObjectIds,
              thickness: DEFAULT_FLOOR_THICKNESS,
              fallbackHeight: ceiling.height,
            },
          ),
    [ceiling, hidden, wallEndpointHeightMap, walls],
  );

  useEffect(() => () => geometry.dispose(), [geometry]);

  const userData = {
    floorplannerType: hidden ? "ceiling-edge" : "ceiling",
    floorplannerId: ceiling.id,
    floorplannerFloorId: ceiling.floorId,
    floorplannerSource: ceiling.source,
  };

  if (hidden) {
    return (
      <lineSegments
        geometry={geometry}
        renderOrder={28}
        userData={userData}
        raycast={disablePointerRaycast}
      >
        <lineBasicMaterial
          color={HIDDEN_EDGE_COLOR}
          transparent
          opacity={HIDDEN_EDGE_OPACITY}
          depthWrite={false}
          depthTest
          toneMapped={false}
        />
      </lineSegments>
    );
  }

  return (
    <mesh
      geometry={geometry}
      castShadow={false}
      receiveShadow
      userData={userData}
    >
      <CatalogStandardMaterial
        color={color}
        defaults={materialDefaults}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
};

export default Ceiling3DLayer;
